// H-16, L-3: the team's notes on a candidate, part of showing a person weighed the
// evidence. The candidate never sees them. Anyone who can open the candidate can add
// one, or edit or delete one, and the Activity view records that they did.

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import InfoTip from '../../../../components/ui/InfoTip';
import { hiringApi, hiringMessage, type HiringCandidateView } from '../../../../api/hiring';
import { useHiringOrg, useRefreshHiring } from '../../../../queries/useHiring';
import { ActionButton, BODY, DANGER, LABEL, TEXT, TEXT_2, TEXT_3, TEXTAREA_SX } from '../capability-setup/designSystem';
import { Empty, GRID_LINE, HEAD_LINE, RowButton, day } from './hiringParts';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

// Added By, Date, Note, Actions, in the shape of the round's candidate table.
const COLUMNS = '170px 150px minmax(240px, 1fr) 170px';
const MIN_WIDTH = 730;
const BRANDY_RULE = '1px solid rgba(247, 148, 32, 0.45)';
const rowSx = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  minHeight: 50,
  boxSizing: 'border-box' as const,
  '& > * + *': { borderLeft: GRID_LINE }
};
const cellSx = { display: 'flex', alignItems: 'center', px: '12px', py: '10px', minWidth: 0 };
const actionsSx = { ...cellSx, justifyContent: 'center', gap: '6px', px: '6px' };
const textSx = { ...BODY, fontSize: 12.5, color: TEXT_2 };

/** What is being written: a new note, or the words of one being edited. */
type Draft = { noteId: string | null; text: string };

const NoteField: React.FC<{ value: string; label: string; onChange: (text: string) => void }> = ({
  value,
  label,
  onChange
}) => (
  <Box
    component="textarea"
    autoFocus
    rows={3}
    aria-label={label}
    value={value}
    maxLength={8000}
    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
    sx={TEXTAREA_SX}
  />
);

const CandidateNotes: React.FC<{ candidate: HiringCandidateView; roundId: string }> = ({ candidate, roundId }) => {
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (work: () => Promise<unknown>, after: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      refresh({ roundId, candidateId: candidate.id });
      after();
    } catch (err) {
      setError(hiringMessage(err, 'The note could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (!draft) return;
    const text = draft.text.trim();
    run(
      () =>
        draft.noteId
          ? hiringApi.editNote(candidate.id, draft.noteId, text, org)
          : hiringApi.addNote(candidate.id, text, org),
      () => setDraft(null)
    );
  };

  const saveActions = (
    <>
      <RowButton brandy disabled={busy || !draft?.text.trim()} onClick={save}>
        Save
      </RowButton>
      <RowButton disabled={busy} onClick={() => setDraft(null)}>
        Cancel
      </RowButton>
    </>
  );

  return (
    <Box
      sx={{
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        border: BRANDY_RULE,
        borderRadius: '8px',
        boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
        [MOBILE_SX]: { p: '12px' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <Typography sx={{ ...LABEL, color: TEXT }}>Your Team&apos;s Notes</Typography>
        <InfoTip title="Candidates do not see these notes. They are for your team only." placement="top" />
        <Box sx={{ flex: 1 }} />
        <ActionButton
          primary
          disabled={draft !== null}
          onClick={() => {
            setDeleting(null);
            setDraft({ noteId: null, text: '' });
          }}
        >
          Add Note
        </ActionButton>
      </Box>

      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}

      {draft && draft.noteId === null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <NoteField label="New note" value={draft.text} onChange={(text) => setDraft({ noteId: null, text })} />
          <Box sx={{ display: 'flex', gap: '6px' }}>{saveActions}</Box>
        </Box>
      )}

      {candidate.notes.length === 0 && !draft && (
        <Empty>No notes yet. Add one to keep your team&apos;s thinking with the candidate.</Empty>
      )}

      {candidate.notes.length > 0 && (
        <Box sx={{ border: GRID_LINE, borderRadius: '4px', overflowX: 'auto' }}>
          <Box sx={{ minWidth: MIN_WIDTH }}>
            <Box sx={{ ...rowSx, borderBottom: HEAD_LINE }}>
              {['Added By', 'Date', 'Note'].map((title) => (
                <Box key={title} sx={{ ...LABEL, ...cellSx, color: TEXT }}>
                  {title}
                </Box>
              ))}
              <Box sx={{ ...LABEL, ...actionsSx, color: TEXT }}>Actions</Box>
            </Box>

            {candidate.notes.map((note, index) => {
              const editing = draft?.noteId === note.id;
              return (
                <Box
                  key={note.id}
                  sx={{ ...rowSx, borderBottom: index < candidate.notes.length - 1 ? GRID_LINE : 'none' }}
                >
                  <Box sx={cellSx}>
                    <Typography sx={{ ...textSx, overflowWrap: 'anywhere' }}>{note.addedByName || '-'}</Typography>
                  </Box>
                  <Box sx={cellSx}>
                    <Typography sx={{ ...textSx, color: TEXT_3 }}>
                      {day(note.addedAt)}
                      {note.editedAt ? ' (edited)' : ''}
                    </Typography>
                  </Box>
                  <Box sx={cellSx}>
                    {editing ? (
                      <NoteField
                        label="Edit note"
                        value={draft.text}
                        onChange={(text) => setDraft({ noteId: note.id, text })}
                      />
                    ) : (
                      <Typography sx={{ ...textSx, color: TEXT, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {note.text}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={actionsSx}>
                    {editing && saveActions}
                    {!editing && deleting === note.id && (
                      <>
                        <RowButton
                          brandy
                          disabled={busy}
                          onClick={() =>
                            run(
                              () => hiringApi.deleteNote(candidate.id, note.id, org),
                              () => setDeleting(null)
                            )
                          }
                        >
                          Confirm
                        </RowButton>
                        <RowButton disabled={busy} onClick={() => setDeleting(null)}>
                          Cancel
                        </RowButton>
                      </>
                    )}
                    {!editing && deleting !== note.id && (
                      <>
                        <RowButton
                          brandy
                          disabled={draft !== null}
                          onClick={() => {
                            setDeleting(null);
                            setDraft({ noteId: note.id, text: note.text });
                          }}
                        >
                          Edit
                        </RowButton>
                        <RowButton disabled={draft !== null} onClick={() => setDeleting(note.id)}>
                          Delete
                        </RowButton>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CandidateNotes;
